import { Alert, Button, DatePicker, Modal, Spin } from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatCurrency, quoteExtendInsuranceSale, roundToHalfHour, INSURANCE_SALE_TIME_PICKER } from "../../lib/insuranceSaleApi";
import PaySaleStep from "./steps/PaySaleStep";

const ExtendInsuranceModal = ({ open, sale, onClose, onSuccess }) => {
    const { t } = useTranslation("sellInsurance");
    const [dropoff, setDropoff] = useState(null);
    const [quote, setQuote] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [quoteError, setQuoteError] = useState(null);

    const currentEnd = sale?.exactDropoffTime || sale?.dropoffDate;

    useEffect(() => {
        if (!open) {
            setDropoff(null);
            setQuote(null);
            setQuoteError(null);
            return;
        }

        setDropoff(
            roundToHalfHour(currentEnd ? dayjs(currentEnd).add(1, "day") : dayjs().add(1, "day")),
        );
    }, [open, currentEnd]);

    useEffect(() => {
        if (!open || !sale?.id || !dropoff) {
            return undefined;
        }

        let cancelled = false;
        setQuoteLoading(true);
        setQuoteError(null);

        quoteExtendInsuranceSale(sale.id, dropoff.toISOString())
            .then((result) => {
                if (!cancelled) setQuote(result);
            })
            .catch((error) => {
                if (!cancelled) {
                    setQuote(null);
                    setQuoteError(error?.response?.data?.message || error?.message);
                }
            })
            .finally(() => {
                if (!cancelled) setQuoteLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, sale?.id, dropoff]);

    return (
        <Modal
            open={open}
            title={t("extend.title", { id: sale?.id })}
            onCancel={onClose}
            footer={null}
            destroyOnClose
            maskClosable={false}
            centered
            width={{
                xs: "calc(100vw - 2rem)",
                sm: 520,
            }}>
            <div className="space-y-4">
                <div>
                    <div className="mb-1 text-sm text-gray-600">{t("extend.currentEnd")}</div>
                    <div>{currentEnd ? dayjs(currentEnd).format("MMM D, YYYY h:mm A") : "—"}</div>
                </div>

                <div>
                    <div className="mb-1 text-sm text-gray-600">{t("extend.newEnd")}</div>
                    <DatePicker
                        showTime={INSURANCE_SALE_TIME_PICKER}
                        format="YYYY-MM-DD HH:mm"
                        minuteStep={30}
                        value={dropoff}
                        onChange={(value) => setDropoff(roundToHalfHour(value))}
                        disabledDate={(value) => value && currentEnd && value.isBefore(dayjs(currentEnd))}
                        style={{ width: "100%" }}
                    />
                </div>

                {quoteLoading && (
                    <div className="flex justify-center py-4">
                        <Spin />
                    </div>
                )}

                {quoteError && <Alert type="error" showIcon message={quoteError} />}

                {quote && !quoteLoading && (
                    <>
                        <Alert
                            type="info"
                            showIcon
                            message={t("extend.quoteSummary", {
                                days: quote.extraDays,
                                total: formatCurrency(quote.extraTotal),
                            })}
                        />
                        <PaySaleStep
                            reservationId={sale.id}
                            amount={Number(quote.extraTotal || 0)}
                            customerEmail={sale.email}
                            reservationProcess="insuranceSaleExtension"
                            extensionDropoffDate={dropoff?.toISOString()}
                            onSuccess={onSuccess}
                        />
                    </>
                )}

                <div className="flex justify-end">
                    <Button onClick={onClose}>{t("wizard.cancel")}</Button>
                </div>
            </div>
        </Modal>
    );
};

ExtendInsuranceModal.propTypes = {
    open: PropTypes.bool.isRequired,
    sale: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
};

ExtendInsuranceModal.defaultProps = {
    sale: null,
    onSuccess: null,
};

export default ExtendInsuranceModal;
